import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Collapse } from 'reactstrap';
import './EmployeeEWALimits.css';

// Define payment method enum for the component
const PaymentMethodTypes = {
  Standard: 'Standard',
  Instant: 'Instant'
} as const;


/**
 * Note: API responses may return numeric values as strings.
 * The EmployeeAdvanceAdd component uses Number() to convert all string numbers to actual numbers.
 * Invalid values become NaN and will throw errors when used in display functions.
 */
export interface IEmployeeLimits {
  volume_and_value: {
    amount_limit: { value: number, default: 0, enabled: boolean };
    request_limit: { value: number, default: 0, enabled: boolean };
  };
  instant: {
    amount_limit: { value: number, default: 0, enabled: boolean };
    request_limit: { value: number, default: 0, enabled: boolean }; 
  };
  standard: {
    amount_limit: { value: number, default: 0, enabled: boolean };
    request_limit: { value: number, default: 0, enabled: boolean };
  };
  value_added_service: {
    amount_limit: { value: number, default: 0, enabled: boolean };
    request_limit: { value: number, default: 0, enabled: boolean };
  };
  employee_limits?: {
    available_advance_balance: number,
    available_advance_to_withdraw: number,
    is_advance_permitted: boolean,
    amount_deducted_next_payslip?: number,
    volume_and_value: {
      amount_used: number;
      requests_used: number;
    },
    value_added_service: {
      amount_used: number;
      requests_used: number;
    },
    instant: {
      amount_used: number;
      requests_used: number;
    },
    standard: {
      amount_used: number;
      requests_used: number;
    }
  }
}

export interface IEmployeeAdvanceData {
  available_advance_balance?: number;
  available_advance_to_withdraw?: number;
  is_advance_permitted?: boolean;
  permission_reason?: string;
  amount_deducted_next_payslip?: number;
  volume_and_value?: {
    amount_used: number;
    requests_used?: number;
    
  };
  value_added_service?: {
    amount_used: number;
    requests_used?: number;
    
  };
  instant?: {
    amount_used: number;
    requests_used?: number;
    
  };
  standard?: {
    amount_used: number;
    requests_used?: number;
    
  };
}

// Export the components
// Helper functions moved outside the component to reduce cognitive complexity
const createCombinedData = (employeeLimits: IEmployeeLimits, employeeAdvanceData: Partial<IEmployeeAdvanceData>) => {
  const empLimits = employeeLimits?.employee_limits;
  return {
    // Balance and deduction values - prioritize employee_limits for available_advance_to_withdraw
    available_advance_balance: employeeAdvanceData?.available_advance_balance ?? 0,
    available_advance_to_withdraw: empLimits?.available_advance_to_withdraw ?? 0,
    amount_deducted_next_payslip: empLimits?.amount_deducted_next_payslip ?? 0,

    // Transaction data: always use employeeLimits.employee_limits if present
    volume_and_value: {
      amount_used: empLimits?.volume_and_value?.amount_used ?? employeeAdvanceData?.volume_and_value?.amount_used ?? 0,
      requests_used: empLimits?.volume_and_value?.requests_used ?? employeeAdvanceData?.volume_and_value?.requests_used ?? 0
    },
    standard: {
      amount_used: empLimits?.standard?.amount_used ?? employeeAdvanceData?.standard?.amount_used ?? 0,
      requests_used: empLimits?.standard?.requests_used ?? employeeAdvanceData?.standard?.requests_used ?? 0
    },
    instant: {
      amount_used: empLimits?.instant?.amount_used ?? employeeAdvanceData?.instant?.amount_used ?? 0,
      requests_used: empLimits?.instant?.requests_used ?? employeeAdvanceData?.instant?.requests_used ?? 0
    },
    value_added_service: {
      amount_used: empLimits?.value_added_service?.amount_used ?? employeeAdvanceData?.value_added_service?.amount_used ?? 0,
      requests_used: empLimits?.value_added_service?.requests_used ?? employeeAdvanceData?.value_added_service?.requests_used ?? 0
    }
  };
};

// Helper function to count active limits
const countActiveLimits = (limits: IEmployeeLimits): number => {
  let count = 0;
  // Check volume_and_value limits
  if (limits.volume_and_value?.amount_limit?.enabled) count += 1;
  if (limits.volume_and_value?.request_limit?.enabled) count += 1;
  // Check standard limits
  if (limits.standard?.amount_limit?.enabled) count += 1;
  if (limits.standard?.request_limit?.enabled) count += 1;
  // Check instant limits
  if (limits.instant?.amount_limit?.enabled) count += 1;
  if (limits.instant?.request_limit?.enabled) count += 1;
  // Check value_added_service limits
  if (limits.value_added_service?.amount_limit?.enabled) count += 1;
  if (limits.value_added_service?.request_limit?.enabled) count += 1;
  return count;
};

// Format currency values
const formatCurrency = (value: number): string => {
  if (Number.isNaN(value)) {
    console.error('[formatCurrency] Invalid value (NaN):', value);
    throw new Error(`formatCurrency received NaN`);
  }
  return `R${value.toFixed(2)}`;
};

// Format transaction counts
const formatTransactionCount = (used: number, total: number): string => `${used} / ${total} taken`;

// Helper function to get the value from a limit object
const getValue = (limit: { value: number, default: number, enabled: boolean } | undefined): number =>
  limit?.enabled ? Number(limit.value) : Number(limit?.value ?? 0);

const EmployeeLimits: React.FC<{ 
  employeeLimits: IEmployeeLimits; 
  employeeAdvanceData?: IEmployeeAdvanceData 
}> = ({ employeeLimits, employeeAdvanceData = {} }) => {
  // Create a simplified combined data object for easier access
  const combinedData = createCombinedData(employeeLimits, employeeAdvanceData);
  
  // Development logging is disabled to comply with ESLint rules
  
  // State for collapsible sections
  const [balancesOpen, setBalancesOpen] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);



  // Render a card header with consistent styling
  const renderCardHeader = (title: string, isOpen: boolean, toggleFn: () => void, count?: number) => (
    <CardHeader onClick={toggleFn} className="cursor-pointer">
      <div className="d-flex justify-content-between align-items-center">
        <span className="text-primary font-weight-bold">{title} {count && count > 0 ? `(${count} active)` : ''}</span>
        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} />
      </div>
    </CardHeader>
  );

  // Render a balance item
  const renderBalanceItem = (label: string, value: number, isPrimary = false) => (
    <div className="limit-item">
      <div className="limit-label">{label}</div>
      <div className={`limit-value ${isPrimary ? 'text-primary' : ''}`}>{formatCurrency(Number(value))}</div>
    </div>
  );

  // Render the balances and deductions card
  const renderBalancesCard = () => (
    <Card className="mb-3">
      {renderCardHeader('Balances & deductions', balancesOpen, () => setBalancesOpen(!balancesOpen))}
      <Collapse isOpen={balancesOpen}>
        <CardBody>
          {renderBalanceItem('Available to withdraw (including limits)', combinedData.available_advance_to_withdraw, true)}
          {renderBalanceItem('Available balance', combinedData.available_advance_balance)}
          {renderBalanceItem('Amount deducted from next payslip', combinedData.amount_deducted_next_payslip, true)}
        </CardBody>
      </Collapse>
    </Card>
  );

  // Get the count of active limits for display
  const activeLimitsCount = countActiveLimits(employeeLimits);

  // String for display when a limit is not enabled
  const notEnabled = "Not enabled for {payment_method}";

  // Render an amount limit item
  const renderAmountLimitItem = (label: string, limit: any, replacementText: string) => (
    <div className="limit-item">
      <div className="limit-label">{label}</div>
      <div className={`limit-value ${!limit?.enabled ? 'not-enabled' : ''}`}>
        {limit?.enabled ?
          formatCurrency(Number(limit.value)) :
          notEnabled.replace("{payment_method}", replacementText)}
      </div>
    </div>
  );

  // Render a transaction limit item
  const renderTransactionLimitItem = (label: string, limit: any, usageData: any, replacementText: string) => (
    <div className="limit-item">
      <div className="limit-label">{label}</div>
      <div className={`limit-value ${!limit?.enabled ? 'not-enabled' : ''}`}>
        {limit?.enabled ? 
          formatTransactionCount(usageData.requests_used, getValue(limit)) : 
          notEnabled.replace("{payment_method}", replacementText)}
      </div>
    </div>
  );

  // Render all amount limits
  const renderAmountLimits = () => (
    <>
      {renderAmountLimitItem(
        "EWA: Total amount per pay cycle", 
        employeeLimits.volume_and_value?.amount_limit, 
        "Total"
      )}
      
      {renderAmountLimitItem(
        "EWA: Per standard transaction", 
        employeeLimits.standard?.amount_limit, 
        PaymentMethodTypes.Standard
      )}
      
      {renderAmountLimitItem(
        "EWA: Per instant transaction", 
        employeeLimits.instant?.amount_limit, 
        PaymentMethodTypes.Instant
      )}
      
      {renderAmountLimitItem(
        "VAS: total amount per pay cycle", 
        employeeLimits.value_added_service?.amount_limit, 
        "VAS"
      )}
    </>
  );

  // Render all transaction limits
  const renderTransactionLimits = () => (
    <>
      {renderTransactionLimitItem(
        "EWA: Total transactions per pay cycle", 
        employeeLimits.volume_and_value?.request_limit, 
        combinedData.volume_and_value, 
        "Total"
      )}
      
      {renderTransactionLimitItem(
        "EWA: Standard transactions per pay cycle", 
        employeeLimits.standard?.request_limit, 
        combinedData.standard, 
        PaymentMethodTypes.Standard
      )}
      
      {renderTransactionLimitItem(
        "EWA: Instant transactions per pay cycle", 
        employeeLimits.instant?.request_limit, 
        combinedData.instant, 
        PaymentMethodTypes.Instant
      )}
      
      {renderTransactionLimitItem(
        "VAS: total transactions per pay cycle", 
        employeeLimits.value_added_service?.request_limit, 
        combinedData.value_added_service, 
        "VAS"
      )}
    </>
  );

  return (
    <div className="limits-container">
      {renderBalancesCard()}

      <Card>
        {renderCardHeader('Limits', limitsOpen, () => setLimitsOpen(!limitsOpen), activeLimitsCount)}
        <Collapse isOpen={limitsOpen}>
          <CardBody>
            <h5 className="font-weight-bold mb-3">Amount limits</h5>
            {renderAmountLimits()}
            
            <h5 className="font-weight-bold mb-3 mt-4">Transaction limits</h5>
            {renderTransactionLimits()}
          </CardBody>
        </Collapse>
      </Card>
    </div>
  );
};

export default EmployeeLimits;